import { closeReconciliationURLs } from 'routes/close/closeURLs';

import { CommentNotificationDisplay } from 'components/comments/CommentNotificationDisplay';
import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { getName } from 'utils/users/config';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';
import { CommentReplyNotificationItem } from 'layout/inbox-notifications/close/reconciliation/task/comment/CommentReplyNotificationItem';

import {
  ReconTaskCommentAddedNotification,
  ReconTaskCommentEditedNotification,
  ReconTaskCommentNotification,
  ReconTaskCommentReplyNotification,
  ReconTaskCommentTaggedNotification,
} from 'types/inbox-notifications/close/ReconciliationNotifications';
import { SegmentAtByDot } from 'types/utils';

type CommentLevelKeys = SegmentAtByDot<ReconTaskCommentNotification['type'], 4>;

export function CommentNotificationItem({
  type,
  item,
}: {
  type: string[];
  item: ReconTaskCommentNotification;
}) {
  const [actionType, ...restOfType] = type;
  if (!item.task || !item.comment) {
    return <div>Reconciliation task not found</div>;
  }

  switch (actionType as CommentLevelKeys) {
    case 'added':
      return (
        <CommentAddedNotificationItem
          item={item as ReconTaskCommentAddedNotification}
        />
      );
    case 'tagged':
      return (
        <CommentTaggedNotificationItem
          item={item as ReconTaskCommentTaggedNotification}
        />
      );
    case 'edited':
      return (
        <CommentEditedNotificationItem
          item={item as ReconTaskCommentEditedNotification}
        />
      );
    case 'reply':
      return (
        <CommentReplyNotificationItem
          item={item as ReconTaskCommentReplyNotification}
          type={restOfType}
        />
      );
    default:
      return (
        <div>
          Unknown reconciliation comment notification type: {actionType}
        </div>
      );
  }
}

function CommentAddedNotificationItem({
  item,
}: {
  item: ReconTaskCommentAddedNotification;
}) {
  const { usersMap } = useUsersData();
  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} added a comment.`}
      description={<CommentNotificationDisplay comment={item.comment} />}
    />
  );
}

function CommentTaggedNotificationItem({
  item,
}: {
  item: ReconTaskCommentTaggedNotification;
}) {
  const { usersMap } = useUsersData();
  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} tagged you in a comment.`}
      description={<CommentNotificationDisplay comment={item.comment} />}
    />
  );
}

function CommentEditedNotificationItem({
  item,
}: {
  item: ReconTaskCommentEditedNotification;
}) {
  const { usersMap } = useUsersData();
  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} updated a comment.`}
      description={<CommentNotificationDisplay comment={item.comment} />}
    />
  );
}
